import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  UserPlus,
  RotateCw,
} from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { crmGatewaySyncService } from "@/services/crmGatewaySyncService";
import {
  usersService,
  type KeycloakOnboardResponse,
} from "@/services/usersService";
import type { UserSummary } from "@/types/entities";

interface BranchOption {
  id: string;
  name: string;
  /** Name of the LE this branch belongs to. Shown as a sublabel when the
   *  branch comes from a sub-entity so the operator can tell duplicates apart
   *  across the LE subtree. */
  parentLeName?: string;
}

interface Props {
  open: boolean;
  legalEntityId: string;
  legalEntityName: string;
  branches: BranchOption[];
  onClose: () => void;
}

type BranchStatus =
  | "COMPLETED"
  | "PENDING"
  | "RUNNING"
  | "FAILED"
  | "UNKNOWN";

const isCompleted = (s: BranchStatus | undefined) =>
  String(s ?? "").toUpperCase() === "COMPLETED";

const isMissingOnboardEmail = (email: string | undefined | null) => {
  const value = email?.trim() ?? "";
  return !value || value.toLowerCase() === "(missing)";
};

function formatOnboardFailure(
  failure: { email: string; reason: string },
  resolvedName?: string,
): string {
  const reason = failure.reason?.trim() || "Unknown error";
  const userLabel =
    resolvedName?.trim() ||
    (!isMissingOnboardEmail(failure.email) ? failure.email.trim() : "") ||
    "A synced user";

  if (
    isMissingOnboardEmail(failure.email) &&
    reason.toLowerCase() === "user row has no email"
  ) {
    return `${userLabel} has no email. Add it in CRM, re-sync Negotiator data, then retry.`;
  }
  if (isMissingOnboardEmail(failure.email)) {
    return `${userLabel} — ${reason}`;
  }
  return `${failure.email.trim()} — ${reason}`;
}

function userDisplayName(user: UserSummary): string {
  return user.name?.trim() || user.externalId?.trim() || user.email?.trim() || "Unknown user";
}

async function loadLegalEntityUsers(legalEntityId: string): Promise<UserSummary[]> {
  const users: UserSummary[] = [];
  let page = 0;
  const size = 100;

  while (page < 50) {
    const res = await usersService.list({ legalEntityId }, { page, size });
    const content = res.content ?? [];
    users.push(...content);
    if (!res.hasNext || content.length < size) break;
    page += 1;
  }

  return users;
}

function resolveOnboardFailureMessages(
  failures: Array<{ email: string; reason: string }>,
  users: UserSummary[],
): string[] {
  const usersByEmail = new Map<string, UserSummary>();
  const usersWithoutEmail: UserSummary[] = [];

  for (const user of users) {
    if (user.active === false) continue;
    const email = user.email?.trim().toLowerCase() ?? "";
    if (!isMissingOnboardEmail(user.email)) usersByEmail.set(email, user);
    else usersWithoutEmail.push(user);
  }

  let missingEmailIndex = 0;
  return failures.map((failure) => {
    if (!isMissingOnboardEmail(failure.email)) {
      const matched = usersByEmail.get(failure.email.trim().toLowerCase());
      return formatOnboardFailure(
        failure,
        matched ? userDisplayName(matched) : failure.email.trim(),
      );
    }

    const matched = usersWithoutEmail[missingEmailIndex];
    if (matched) missingEmailIndex += 1;
    return formatOnboardFailure(
      failure,
      matched ? userDisplayName(matched) : undefined,
    );
  });
}

const statusBadge = (status: BranchStatus | undefined) => {
  const s = String(status ?? "UNKNOWN").toUpperCase();
  let cls = "bg-slate-100 text-slate-600 border-slate-200";
  if (s === "COMPLETED")
    cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (s === "PENDING" || s === "RUNNING" || s === "QUEUED")
    cls = "bg-blue-50 text-blue-700 border-blue-200";
  else if (s === "FAILED" || s === "ERROR")
    cls = "bg-red-50 text-red-700 border-red-200";
  return (
    <span
      className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md border ${cls}`}
    >
      {s}
    </span>
  );
};

/**
 * Onboards LE users to Keycloak via atlas-api.
 *
 * Flow: the operator first selects which branches they want to onboard, then
 * clicks Onboard Users. At that point we verify NEGOTIATOR sync status for
 * the root legal entity of the selected branches; if it is COMPLETED we POST to atlas-api
 * (which talks to the Keycloak admin API). If it is not
 * COMPLETED, we surface its status inline and ask the user to wait — we do
 * NOT submit. The user can adjust selection and try again.
 */
export const OnboardUsersModal: React.FC<Props> = ({
  open,
  legalEntityId,
  legalEntityName,
  branches,
  onClose,
}) => {
  const [statuses, setStatuses] = useState<Record<string, BranchStatus>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<KeycloakOnboardResponse | null>(null);
  const [scopeUsers, setScopeUsers] = useState<UserSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [waitNotice, setWaitNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStatuses({});
    setSelected({});
    setChecking(false);
    setSubmitting(false);
    setResult(null);
    setScopeUsers([]);
    setError(null);
    setWaitNotice(null);
  }, [open, legalEntityId, branches]);

  const selectedBranchIds = useMemo(
    () => branches.filter((b) => selected[b.id]).map((b) => b.id),
    [branches, selected],
  );

  const busy = checking || submitting;

  useEffect(() => {
    if (!result?.failed?.length || !legalEntityId) {
      setScopeUsers([]);
      return;
    }

    let cancelled = false;
    loadLegalEntityUsers(legalEntityId)
      .then((users) => {
        if (!cancelled) setScopeUsers(users);
      })
      .catch(() => {
        if (!cancelled) setScopeUsers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [result, legalEntityId]);

  const failureMessages = useMemo(() => {
    const failures = result?.failed ?? [];
    if (!failures.length) return [];
    return resolveOnboardFailureMessages(failures, scopeUsers);
  }, [result, scopeUsers]);

  const handleToggle = (branchId: string) => {
    if (busy) return;
    // Clearing the wait notice once the user changes selection avoids stale
    // messaging when they react to a "please wait" by unticking the branch.
    setWaitNotice(null);
    setSelected((prev) => ({ ...prev, [branchId]: !prev[branchId] }));
  };

  const handleSubmit = async () => {
    if (busy || selectedBranchIds.length === 0) return;
    setError(null);
    setResult(null);
    setWaitNotice(null);

    // 1) Verify NEGOTIATOR sync for the selected branches' root legal entity.
    setChecking(true);
    let statusMap: Record<string, BranchStatus> = {};
    try {
      const res = await crmGatewaySyncService.checkSyncReadiness({
        branchIds: selectedBranchIds,
        entityType: "NEGOTIATOR",
      });
      if (res?.branches) {
        for (const id of selectedBranchIds) {
          statusMap[id] = (res.branches[id] as BranchStatus) ?? "UNKNOWN";
        }
      } else {
        const fallback: BranchStatus = res?.ready ? "COMPLETED" : "PENDING";
        for (const id of selectedBranchIds) statusMap[id] = fallback;
      }
    } catch (err: any) {
      setChecking(false);
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to check Negotiator sync status",
      );
      return;
    } finally {
      setStatuses((prev) => ({ ...prev, ...statusMap }));
      setChecking(false);
    }

    const incomplete = selectedBranchIds.filter(
      (id) => !isCompleted(statusMap[id]),
    );
    if (incomplete.length > 0) {
      setWaitNotice(
        `Negotiator sync is not yet complete for the root legal entity of the selected branch${incomplete.length === 1 ? "" : "es"}. Please wait until it finishes, then try again.`,
      );
      return;
    }

    // 2) All selected branches are COMPLETED — fire the onboarding request.
    setSubmitting(true);
    try {
      const res = await usersService.onboardToKeycloak({
        legalEntityId,
        branchIds: selectedBranchIds,
      });
      setResult(res);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to onboard users",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const { onOverlayClick } = useModalDismiss(open && !busy, onClose);

  if (!open) return null;

  const submitDisabled = busy || selectedBranchIds.length === 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-ink-900/40 p-6 pt-14 backdrop-blur-sm animate-in fade-in duration-200 sm:p-10 sm:pt-16"
      onClick={onOverlayClick}
    >
      <div className="flex max-h-[calc(100vh-7rem)] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-200 sm:max-h-[calc(100vh-8rem)]">
        <div className="flex items-center justify-between border-b border-bridge-border bg-slate-50/60 px-7 pb-5 pt-6">
          <div>
            <h3 className="text-lg font-bold text-bridge-fg flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-bridge-accent" />
              Onboard Users
            </h3>
            <p className="text-xs text-bridge-muted mt-0.5">
              Legal Entity{" "}
              <strong className="text-bridge-fg">{legalEntityName}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="p-1.5 text-bridge-muted hover:text-bridge-fg hover:bg-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6 overflow-y-auto px-7 pb-6 pr-8 pt-8">
          {!result && (
            <>
              <p className="text-sm text-slate-600">
                Select the branches whose users you want to onboard, then
                click Onboard Users. Atlas will check that Negotiator sync is
                complete for the root legal entity before creating or linking
                them in Keycloak.
              </p>

              {branches.length === 0 ? (
                <div className="text-sm text-bridge-muted italic px-2 py-3">
                  This legal entity has no branches to onboard.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end px-1 pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        const allSelected = branches.every((b) => selected[b.id]);
                        const next: Record<string, boolean> = {};
                        if (!allSelected) {
                          branches.forEach((b) => (next[b.id] = true));
                        }
                        setSelected(next);
                      }}
                      className="text-xs font-semibold text-bridge-accent hover:text-bridge-accent/80 transition-colors"
                    >
                      {branches.every((b) => selected[b.id]) ? "Deselect All" : "Select All"}
                    </button>
                  </div>
                  <ul className="space-y-3">
                  {branches.map((branch) => {
                    const st = statuses[branch.id];
                    const checked = !!selected[branch.id];
                    const showsBlocker = checked && st && !isCompleted(st);
                    return (
                      <li
                        key={branch.id}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${
                          showsBlocker
                            ? "bg-amber-50/50 border-amber-200"
                            : "bg-white border-slate-200"
                        }`}
                      >
                        <input
                          id={`onboard-br-${branch.id}`}
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggle(branch.id)}
                          disabled={busy}
                          className="w-4 h-4 accent-bridge-accent shrink-0"
                        />
                        <label
                          htmlFor={`onboard-br-${branch.id}`}
                          className="flex-1 min-w-0 cursor-pointer"
                        >
                          <div className="text-sm font-bold text-bridge-fg leading-tight">
                            {branch.name}
                          </div>
                          {branch.parentLeName && (
                            <div className="text-[11px] text-bridge-muted mt-0.5">
                              {branch.parentLeName}
                            </div>
                          )}
                          {showsBlocker && (
                            <div className="mt-0.5 text-[11px] text-amber-700">
                              Please wait — Negotiator sync is not yet
                              complete for the root legal entity.
                            </div>
                          )}
                        </label>
                        {st && <div className="shrink-0">{statusBadge(st)}</div>}
                      </li>
                    );
                  })}
                </ul>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-emerald-200 bg-emerald-50/60">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                <div className="text-sm text-emerald-800">
                  <div className="font-bold">Onboarding finished</div>
                  <div className="text-xs text-emerald-700/80 mt-1 space-x-3">
                    <span>
                      <strong>{result.created}</strong> created
                    </span>
                    <span>
                      <strong>{result.linked}</strong> already existed
                      (linked)
                    </span>
                    <span>
                      <strong>{result.skipped}</strong> skipped (already
                      synced)
                    </span>
                  </div>
                </div>
              </div>

              {result.failed && result.failed.length > 0 && (
                <div className="px-4 py-3 rounded-xl border border-red-200 bg-red-50/60">
                  <div className="text-sm font-bold text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {result.failed.length} failed
                  </div>
                  <ul className="mt-2 text-[11px] text-red-700 space-y-0.5">
                    {result.failed.slice(0, 6).map((f, index) => {
                      const message =
                        failureMessages[index] ?? formatOnboardFailure(f);
                      return (
                        <li
                          key={`${f.email}-${f.reason}-${index}`}
                          className="leading-snug"
                          title={message}
                        >
                          {message}
                        </li>
                      );
                    })}
                    {result.failed.length > 6 && (
                      <li>… and {result.failed.length - 6} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {!result && (checking || waitNotice || error) && (
          <div className="space-y-2 border-t border-bridge-border bg-white px-7 py-4">
            {checking && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="w-4 h-4 animate-spin text-bridge-accent" />
                Checking Negotiator sync status…
              </div>
            )}

            {waitNotice && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-xl border border-amber-200 bg-amber-50/60 text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {waitNotice}
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-xl border border-red-200 bg-red-50/60 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-4 border-t border-bridge-border bg-slate-50/40 px-7 py-5">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl h-10 px-4"
          >
            Close
          </Button>
          {!result && (
            <Button
              onClick={handleSubmit}
              disabled={submitDisabled}
              loading={busy}
              className="rounded-xl h-10 px-5 bg-bridge-accent hover:bg-bridge-accent/90 text-white font-bold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <UserPlus className="w-4 h-4" />
              Onboard Users
            </Button>
          )}
          {result && (result.failed?.length ?? 0) > 0 && (
            <Button
              onClick={handleSubmit}
              loading={busy}
              className="rounded-xl h-10 px-5 bg-bridge-accent hover:bg-bridge-accent/90 text-white font-bold inline-flex items-center gap-2"
            >
              <RotateCw className="w-4 h-4" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
